import React, { useState, useEffect, useCallback } from 'react';
import { Target, Globe2, MousePointerClick, Tag, Link2, AlertCircle, ChevronDown, Sparkles, ShoppingCart, Users, Download, Eye, CalendarCheck, BarChart3 } from 'lucide-react';
import { useColorTheme } from '../../context/ColorThemeContext';

const OBJECTIVES = [
  { id: 'sales', label: 'Sales', icon: ShoppingCart },
  { id: 'lead_generation', label: 'Lead Gen', icon: Users },
  { id: 'app_installs', label: 'App Installs', icon: Download },
  { id: 'website_traffic', label: 'Traffic', icon: Globe2 },
  { id: 'brand_awareness', label: 'Awareness', icon: Eye },
  { id: 'event_registrations', label: 'Events', icon: CalendarCheck },
];

const PLATFORMS = [
  { id: 'instagram', label: 'Instagram', color: 'from-pink-500 to-purple-600' },
  { id: 'facebook', label: 'Facebook', color: 'from-blue-600 to-blue-500' },
  { id: 'linkedin', label: 'LinkedIn', color: 'from-blue-700 to-cyan-600' },
  { id: 'google_display', label: 'Google Ads', color: 'from-red-500 to-yellow-500' },
  { id: 'tiktok', label: 'TikTok', color: 'from-slate-800 to-pink-500' },
  { id: 'youtube', label: 'YouTube', color: 'from-red-600 to-red-500' },
];

const CTA_GOALS = [
  { id: 'buy_now', label: 'Buy Now' },
  { id: 'sign_up', label: 'Sign Up' },
  { id: 'book_appointment', label: 'Book' },
  { id: 'download_app', label: 'Download' },
  { id: 'learn_more', label: 'Learn More' },
];

const OBJECTIVE_TO_CTA = {
  sales: 'buy_now',
  lead_generation: 'sign_up',
  app_installs: 'download_app',
  website_traffic: 'learn_more',
  brand_awareness: 'learn_more',
  event_registrations: 'sign_up',
};

const CATEGORY_TO_OBJECTIVE = {
  'e-commerce': 'sales',
  'ecommerce': 'sales',
  'retail': 'sales',
  'shopping': 'sales',
  'education': 'event_registrations',
  'school': 'event_registrations',
  'course': 'lead_generation',
  'saas': 'lead_generation',
  'software': 'lead_generation',
  'app': 'app_installs',
  'mobile': 'app_installs',
  'healthcare': 'book_appointment',
  'restaurant': 'website_traffic',
  'blog': 'website_traffic',
  'news': 'website_traffic',
  'event': 'event_registrations',
  'conference': 'event_registrations',
};

export const CampaignStrategy = ({ values, onChange, productCategory, productDescription }) => {
  const { colorTheme } = useColorTheme();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const themeAccent = colorTheme === 'purple' ? 'purple' : colorTheme === 'blue' ? 'blue' : 'emerald';
  const chipBase = `px-3 py-2 rounded-xl text-xs font-medium border cursor-pointer transition-all duration-200 flex items-center gap-1.5 select-none`;
  const chipActive = `bg-${themeAccent}-100 dark:bg-${themeAccent}-500/20 border-${themeAccent}-400 dark:border-${themeAccent}-500/50 text-${themeAccent}-700 dark:text-${themeAccent}-200 ring-1 ring-${themeAccent}-400/30`;
  const chipInactive = `bg-white/60 dark:bg-slate-800/40 border-slate-200 dark:border-slate-700/50 text-slate-600 dark:text-slate-400 hover:border-${themeAccent}-300 dark:hover:border-${themeAccent}-600/40 hover:bg-${themeAccent}-50/50 dark:hover:bg-${themeAccent}-900/10`;

  // AI auto-suggestion: set objective from category
  useEffect(() => {
    if (!values.campaign_objective && productCategory) {
      const cat = productCategory.toLowerCase();
      for (const [key, obj] of Object.entries(CATEGORY_TO_OBJECTIVE)) {
        if (cat.includes(key)) {
          onChange('campaign_objective', obj);
          break;
        }
      }
    }
  }, [productCategory, values.campaign_objective, onChange]);

  // Auto-select CTA when objective changes
  useEffect(() => {
    if (values.campaign_objective) {
      const suggestedCta = OBJECTIVE_TO_CTA[values.campaign_objective];
      if (suggestedCta && !values.cta_goal) {
        onChange('cta_goal', suggestedCta);
      }
    }
  }, [values.campaign_objective, values.cta_goal, onChange]);

  // AI auto-suggest pain point from description
  const suggestPainPoint = useCallback(() => {
    if (productDescription && !values.pain_point) {
      const desc = productDescription.toLowerCase();
      if (desc.includes('time') || desc.includes('fast') || desc.includes('quick')) {
        onChange('pain_point', 'Wasting time on manual processes');
      } else if (desc.includes('cost') || desc.includes('save') || desc.includes('affordable')) {
        onChange('pain_point', 'High costs and budget constraints');
      } else if (desc.includes('quality') || desc.includes('premium') || desc.includes('best')) {
        onChange('pain_point', 'Struggling to find quality solutions');
      } else if (desc.includes('learn') || desc.includes('education') || desc.includes('skill')) {
        onChange('pain_point', 'Lack of accessible learning resources');
      }
    }
  }, [productDescription, values.pain_point, onChange]);

  const sectionClass = `p-4 rounded-2xl border backdrop-blur-xl bg-white/50 dark:bg-slate-800/30 border-slate-200/50 dark:border-slate-700/40`;

  return (
    <div className={sectionClass} data-testid="campaign-strategy-section">
      <div className="flex items-center gap-2 mb-4">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${themeAccent}-100 dark:bg-${themeAccent}-500/20`}>
          <BarChart3 className={`w-4 h-4 text-${themeAccent}-600 dark:text-${themeAccent}-300`} />
        </div>
        <div>
          <h4 className="text-sm font-semibold text-slate-800 dark:text-white">Campaign Strategy</h4>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">AI adapts creative style based on your goals</p>
        </div>
        <span className={`ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full bg-${themeAccent}-100 dark:bg-${themeAccent}-500/20 text-${themeAccent}-600 dark:text-${themeAccent}-300`}>
          Smart Defaults
        </span>
      </div>

      {/* Campaign Objective */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-2">
          <Target className="w-3.5 h-3.5 inline mr-1" />Campaign Objective
        </label>
        <div className="flex flex-wrap gap-2" data-testid="campaign-objective-chips">
          {OBJECTIVES.map(obj => {
            const Icon = obj.icon;
            const isActive = values.campaign_objective === obj.id;
            return (
              <button
                key={obj.id}
                type="button"
                onClick={() => {
                  onChange('campaign_objective', obj.id);
                  onChange('cta_goal', OBJECTIVE_TO_CTA[obj.id] || '');
                }}
                className={`${chipBase} ${isActive ? chipActive : chipInactive}`}
                data-testid={`objective-${obj.id}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {obj.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Primary Platform */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-2">
          <Globe2 className="w-3.5 h-3.5 inline mr-1" />Primary Platform
        </label>
        <div className="flex flex-wrap gap-2" data-testid="platform-chips">
          {PLATFORMS.map(plat => {
            const isActive = values.primary_platform === plat.id;
            return (
              <button
                key={plat.id}
                type="button"
                onClick={() => onChange('primary_platform', plat.id)}
                className={`${chipBase} ${isActive ? chipActive : chipInactive}`}
                data-testid={`platform-${plat.id}`}
              >
                {plat.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* CTA Goal */}
      <div className="mb-3">
        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-2">
          <MousePointerClick className="w-3.5 h-3.5 inline mr-1" />Call-to-Action Goal
        </label>
        <div className="flex flex-wrap gap-2" data-testid="cta-goal-chips">
          {CTA_GOALS.map(cta => {
            const isActive = values.cta_goal === cta.id;
            return (
              <button
                key={cta.id}
                type="button"
                onClick={() => onChange('cta_goal', cta.id)}
                className={`${chipBase} ${isActive ? chipActive : chipInactive}`}
                data-testid={`cta-${cta.id}`}
              >
                {cta.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Advanced Strategy Toggle */}
      <button
        type="button"
        onClick={() => setShowAdvanced(!showAdvanced)}
        className={`flex items-center gap-1.5 text-xs font-medium mt-2 text-${themeAccent}-600 dark:text-${themeAccent}-400 hover:text-${themeAccent}-700 dark:hover:text-${themeAccent}-300 transition-colors`}
        data-testid="advanced-strategy-toggle"
      >
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
        {showAdvanced ? 'Hide' : 'Show'} Advanced Strategy
      </button>

      {showAdvanced && (
        <div className="mt-3 space-y-3 pt-3 border-t border-slate-200/50 dark:border-slate-700/40">
          {/* Offer/Incentive */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">
              <Tag className="w-3.5 h-3.5 inline mr-1" />Offer / Incentive
              <span className="text-slate-400 ml-1">(Optional)</span>
            </label>
            <input
              type="text"
              value={values.offer_incentive || ''}
              onChange={(e) => onChange('offer_incentive', e.target.value)}
              placeholder="e.g., 20% OFF, Free Trial, Limited Seats"
              className={`w-full px-3.5 py-2.5 text-sm border rounded-xl focus:ring-2 focus:border-transparent dark:bg-slate-800/50 dark:text-white transition-colors border-slate-200 dark:border-slate-700/50 focus:ring-${themeAccent}-500`}
              data-testid="offer-incentive-input"
            />
          </div>

          {/* Landing Page URL */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">
              <Link2 className="w-3.5 h-3.5 inline mr-1" />Landing Page URL
              <span className="text-slate-400 ml-1">(Optional)</span>
            </label>
            <input
              type="url"
              value={values.landing_page_url || ''}
              onChange={(e) => onChange('landing_page_url', e.target.value)}
              placeholder="https://yoursite.com/landing-page"
              className={`w-full px-3.5 py-2.5 text-sm border rounded-xl focus:ring-2 focus:border-transparent dark:bg-slate-800/50 dark:text-white transition-colors border-slate-200 dark:border-slate-700/50 focus:ring-${themeAccent}-500`}
              data-testid="landing-page-url-input"
            />
          </div>

          {/* Pain Point */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">
              <AlertCircle className="w-3.5 h-3.5 inline mr-1" />Customer Pain Point
              <span className="text-slate-400 ml-1">(Optional)</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={values.pain_point || ''}
                onChange={(e) => onChange('pain_point', e.target.value)}
                placeholder="Biggest problem your product solves"
                className={`flex-1 px-3.5 py-2.5 text-sm border rounded-xl focus:ring-2 focus:border-transparent dark:bg-slate-800/50 dark:text-white transition-colors border-slate-200 dark:border-slate-700/50 focus:ring-${themeAccent}-500`}
                data-testid="pain-point-input"
              />
              <button
                type="button"
                onClick={suggestPainPoint}
                className={`px-3 py-2 rounded-xl text-xs font-medium bg-${themeAccent}-100 dark:bg-${themeAccent}-500/20 text-${themeAccent}-600 dark:text-${themeAccent}-300 hover:bg-${themeAccent}-200 dark:hover:bg-${themeAccent}-500/30 transition-colors flex items-center gap-1`}
                title="AI Suggest"
                data-testid="ai-suggest-pain-point"
              >
                <Sparkles className="w-3.5 h-3.5" />
                AI
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Export constants for use in backend data enrichment
export const CAMPAIGN_OBJECTIVES = OBJECTIVES;
export const CAMPAIGN_PLATFORMS = PLATFORMS;
export const CAMPAIGN_CTA_GOALS = CTA_GOALS;
export const OBJECTIVE_CTA_MAP = OBJECTIVE_TO_CTA;
